import type { ReactNode } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";

export const Route = createFileRoute("/privacy")({
  component: PrivacyPolicyPage,
});

// Section 7 names every outside service Chat sends personal data to. Update it
// when production starts using another provider (patient/AGENTS.md).

const CONTACT = "nils.zaenker@ivelion.com";

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="mb-8">
      <h2 className="text-2xl font-semibold text-gray-900 mb-4">{title}</h2>
      {children}
    </section>
  );
}

function ContactLink() {
  return (
    <a
      href={`mailto:${CONTACT}`}
      className="text-purple-600 hover:text-purple-700"
    >
      {CONTACT}
    </a>
  );
}

function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Datenschutzhinweise
        </h1>
        <p className="text-gray-500 mb-8">Version: 02.11.2026</p>

        <div className="prose prose-gray max-w-none">
          <Section title="1. Verantwortlicher">
            <p className="mb-4">
              Verantwortlich nach Art. 4 Nr. 7 DSGVO: Ivelion Health GmbH,
              Altensteinstraße 40, 14195 Berlin, Deutschland (nachfolgend
              „wir"). Kontakt: <ContactLink />.
            </p>
          </Section>

          <Section title="2. Zwecke der Verarbeitung">
            <p className="mb-4">
              Bereitstellung und Betrieb der Ivelion-Plattform inkl.
              MS-Chatbot, Missbrauchsvermeidung, IT-Sicherheit, Support,
              Qualitätsverbesserung. Bei Eingabe gesundheitsbezogener
              Informationen verarbeiten wir diese zur Antwortgenerierung. Zur
              Auswertung des Forschungsprojektes werden anonymisiert jene Daten
              erhoben, die freiwillig vom Nutzer angegeben werden. Hierzu
              gehören beispielsweise Indikation, Geschlecht, Alter, Interesse an
              Studien oder anderen spezifischen Angeboten. Diese Daten werden im
              Rahmen des Forschungsprojektes aggregiert und analysiert.
              Anschließend werden die Auswertungsergebnisse wissenschaftlich
              publiziert. Die analysierten und publizierten Daten sind
              anonymisiert und können nicht mit Ihrer Person in Verbindung
              gebracht werden.
            </p>
            <p className="mb-4">
              Zur Verbesserung der Nutzererfahrung ermitteln wir zu Beginn einer
              Chat-Sitzung Ihre ungefähre geografische Position (Stadt,
              Bundesland und Koordinaten) basierend auf Ihrer IP-Adresse. Diese
              Information hilft uns dabei, Sie mit nahegelegenen klinischen
              Studienzentren zu verbinden, die Entfernung zu Studienzentren zu
              berechnen, und die geografische Verteilung unserer Nutzer zu
              analysieren. Die IP-Adresse selbst wird nicht gespeichert; wir
              speichern nur die abgeleiteten Standortdaten.
            </p>
            <p className="mb-4">
              <strong>Konto (freiwillig).</strong> Sie können ein Konto mit
              E-Mail-Adresse und Passwort anlegen. Dann speichern wir Ihre Chats
              in Ihrem Konto, damit Sie sie später und auf anderen Geräten
              wieder aufrufen können. Zum Konto gehören E-Mails, die wir nur für
              das Konto senden: Bestätigung der E-Mail-Adresse, Zurücksetzen
              des Passworts und Hinweise zur Registrierung. Werbung senden wir
              nicht.
            </p>
          </Section>

          <Section title="3. Rechtsgrundlagen">
            <ul className="mb-4">
              <li>
                Art. 6 Abs. 1 lit. b DSGVO: Anlegen und Führen Ihres Kontos,
                Anmeldung und die E-Mails zum Konto.
              </li>
              <li>
                Art. 6 Abs. 1 lit. f DSGVO: Betrugs-/Missbrauchsabwehr, Logs,
                Basis-Analytics (nur minimal, falls berechtigt).
              </li>
              <li>
                Art. 6 Abs. 1 lit. c DSGVO: Rechtspflichten (z. B.
                Nachweispflichten, Sicherheitsvorfälle).
              </li>
              <li>
                Art. 6 Abs. 1 lit. a i. V. m. Art. 9 Abs. 2 lit. a DSGVO:
                ausdrückliche Einwilligung für Gesundheitsdaten und optionale
                Features.
              </li>
              <li>
                Art. 9 Abs. 2 lit. a DSGVO: Ihre ausdrückliche Einwilligung beim
                Anlegen des Kontos, dass wir Ihre Chats, die Gesundheitsdaten
                enthalten können, in Ihrem Konto speichern. Sie widerrufen sie,
                indem Sie Ihr Konto löschen.
              </li>
            </ul>
          </Section>

          <Section title="4. Kategorien personenbezogener Daten">
            <ul className="mb-4">
              <li>
                <strong>Nutzungs-/Technikdaten:</strong> Ungefähre Standortdaten
                (Stadt, Bundesland und Koordinaten, abgeleitet aus IP-Adresse zu
                Beginn der Chat-Sitzung), Zeitstempel, Geräte-/Browserdaten,
                Logfiles. Die IP-Adresse selbst wird nicht dauerhaft
                gespeichert.
              </li>
              <li>
                <strong>Kontodaten:</strong> E-Mail-Adresse, optional Ihr
                Vorname, Ihr Passwort (nur als Hash in unserem Anmeldedienst
                gespeichert), Zeitpunkt der Registrierung und der
                E-Mail-Bestätigung.
              </li>
              <li>
                <strong>Anmeldedaten:</strong> Für jede Anmeldung speichern wir,
                wann sie begonnen hat, wann sie zuletzt genutzt wurde und wann
                sie abläuft.
              </li>
              <li>
                <strong>Chat-Inhalte:</strong> Eingaben und generierte
                Antworten; können Gesundheitsdaten enthalten.
              </li>
              <li>
                <strong>Spracheingaben:</strong> Wenn Sie die Spracheingabe
                nutzen, die Audioaufnahme, die wir in Text umwandeln lassen. Wir
                speichern nur den Text.
              </li>
              <li>
                <strong>E-Mail-Entwürfe:</strong> Betreff/Inhalt,
                Empfängerangaben, Metadaten.
              </li>
              <li>
                <strong>Cookies/Local Storage:</strong> eine technisch
                notwendige Sitzungs-ID im Local Storage und, wenn Sie angemeldet
                sind, das Anmelde-Cookie „chat_account".
              </li>
            </ul>
          </Section>

          <Section title="5. Anonyme Nutzung">
            <p className="mb-4">
              Sie können den Dienst ohne Konto nutzen. Ihre Chats sind dann an
              eine Sitzungs-ID in Ihrem Browser gebunden. Wenn Sie in diesem
              Browser ein Konto anlegen oder sich anmelden, übernehmen wir die
              Chats, die Sie dort ohne Konto geführt haben, in Ihr Konto.
              Minimierte Sicherheitslogs fallen an.
            </p>
          </Section>

          <Section title="6. Speicherdauer">
            <ul className="mb-4">
              <li>
                Chat-Inhalte: Werden für die Dauer der Nutzung und darüber
                hinaus zum Betrieb, zur Verbesserung des Dienstes und zu
                Forschungszwecken gespeichert. Anonymisierte Chatinhalte ohne
                Personenbezug (wie die Indikation, um die es ging oder die
                Studie, an der Interesse gezeigt wurde), werden darüber hinaus
                zu Forschungszwecken aufgenommen und analysiert. Sie können
                jederzeit die Löschung Ihrer Chat-Inhalte gemäß Art. 17 DSGVO
                verlangen.
              </li>
              <li>
                Konto und Chats im Konto: bis Sie einen Chat oder Ihr Konto
                löschen. Sie können Ihr Konto jederzeit auf der Kontoseite
                löschen. Dabei löschen wir sofort Ihre Chats samt unseren
                technischen Aufzeichnungen dazu, Ihre Anmeldung und Ihre
                Kontodaten. Übrig bleiben nur Nutzungszahlen ohne Bezug zu Ihrer
                Person, etwa die Anzahl der Nachrichten pro Tag. In
                Sicherungskopien bleiben die Daten bis zu sechs Monate erhalten.
              </li>
              <li>
                Nicht bestätigte Konten: bleiben bestehen, bis Sie Ihre
                E-Mail-Adresse bestätigen oder die Löschung verlangen.
              </li>
              <li>
                Anmeldungen: 30 Tage, danach melden Sie sich neu an. Beim
                Abmelden endet die Anmeldung sofort, beim Ändern des Passworts
                enden alle Anmeldungen.
              </li>
              <li>
                Links in E-Mails: Bestätigungslinks gelten 48 Stunden, Links zum
                Zurücksetzen des Passworts eine Stunde.
              </li>
              <li>
                Logs/Sicherheitsdaten: 30 Tage, nur zu
                Sicherheits-/Fehlerzwecken.
              </li>
            </ul>
            <p className="mb-0">
              Gesetzliche Aufbewahrungspflichten bleiben unberührt.
            </p>
          </Section>

          <Section title="7. Empfänger und Auftragsverarbeiter">
            <p className="mb-4">
              Wir setzen Dienstleister nach Art. 28 DSGVO ein (AVV
              abgeschlossen):
            </p>
            <ul className="mb-4">
              <li>
                Hosting: Hetzner Online GmbH, Rechenzentrum in Nürnberg,
                Deutschland. Dort liegen unsere Datenbanken, auch Ihr Konto.
              </li>
              <li>
                Netzwerk und Schutz vor Angriffen: Cloudflare, Inc., USA. Alle
                Anfragen an den Dienst laufen verschlüsselt über Cloudflare.
              </li>
              <li>
                KI-Modell: OpenRouter, Inc., USA. OpenRouter leitet Ihre
                Chat-Eingaben an den Anbieter des Sprachmodells weiter, derzeit
                Anthropic PBC, USA (Modell Claude). Wir lassen nur Anbieter zu,
                die Ihre Eingaben weder speichern noch zum Training verwenden.
              </li>
              <li>
                Spracheingabe: DeepInfra, Inc., USA, wandelt Ihre
                Audioaufnahme in Text um.
              </li>
              <li>
                Standortbestimmung: ip-api.com erhält zu Beginn einer
                Chat-Sitzung Ihre IP-Adresse und gibt den ungefähren Standort
                zurück.
              </li>
              <li>
                E-Mails an Studienzentren und Feedback: Resend, USA, versendet
                die Anfrage-E-Mails, die Sie im Chat abschicken, und leitet Ihr
                Feedback per E-Mail an unser Team weiter.
              </li>
              <li>
                E-Mails zu Ihrem Konto: Google Workspace (Google Ireland
                Limited, Irland).
              </li>
            </ul>
            <p className="mb-4">
              Ihr Konto liegt in unserem eigenen Anmeldedienst (Keycloak), den
              wir selbst bei Hetzner betreiben, im selben Anmeldedienst wie
              unsere Software für Fachpersonal. Fachpersonal und Studienzentren
              haben keinen Zugriff auf Ihr Konto oder Ihre Chats.
            </p>
            <p className="mb-4">
              Bei Anbietern in den USA übermitteln wir Daten in ein Drittland.
              Grundlage ist die Zertifizierung des Anbieters nach dem EU-US
              Data Privacy Framework (Art. 45 DSGVO) oder, wo diese fehlt, die
              Standardvertragsklauseln der EU-Kommission (Art. 46 Abs. 2 lit. c
              DSGVO).
            </p>
          </Section>

          <Section title="8. Sicherheit">
            <ul className="mb-4">
              <li>
                Verschlüsselung in Transit (TLS) und - soweit möglich - at rest.
              </li>
              <li>
                Rollen-/Rechtekonzept (Need-to-know), Zugriffprotokollierung.
              </li>
              <li>
                Regelmäßige Updates, Backups, Schwachstellen- und ggf.
                Pen-Tests.
              </li>
            </ul>
          </Section>

          <Section title="9. Cookies und ähnliche Technologien">
            <p className="mb-4">
              Wir nutzen technisch notwendige Cookies (z. B. Session, CSRF,
              Load-Balancing).
            </p>
            <p className="mb-4">
              Wenn Sie sich anmelden, setzen wir das Cookie „chat_account". Es
              enthält eine zufällige Kennung Ihrer Anmeldung, ist für Skripte
              nicht lesbar und wird nur verschlüsselt übertragen. Es gilt 30
              Tage oder bis zum Abmelden. Ohne dieses Cookie ist eine Anmeldung
              nicht möglich, daher ist es technisch notwendig (§ 25 Abs. 2 Nr.
              2 TDDDG).
            </p>
          </Section>

          <Section title="10. Automatisierte Entscheidungen/Profiling">
            <p className="mb-0">
              Es finden keine automatisierten Einzelentscheidungen mit
              Rechtswirkung statt. Personalisierungen (z. B. gespeicherte
              Präferenzen) erfolgen nur mit Einwilligung.
            </p>
          </Section>

          <Section title="11. Ihre Rechte">
            <ul className="mb-4">
              <li>
                Auskunft (Art. 15 DSGVO), Berichtigung (Art. 16), Löschung (Art.
                17), Einschränkung (Art. 18).
              </li>
              <li>Datenübertragbarkeit (Art. 20 DSGVO).</li>
              <li>
                Widerspruch gegen Verarbeitungen auf Basis von Art. 6 Abs. 1
                lit. f DSGVO (Art. 21).
              </li>
              <li>
                Widerruf von Einwilligungen jederzeit mit Wirkung für die
                Zukunft (Art. 7 Abs. 3).
              </li>
              <li>
                Beschwerderecht bei einer Aufsichtsbehörde, z. B. an Ihrem
                Wohnsitz oder am Sitz des Verantwortlichen.
              </li>
            </ul>
            <p className="mb-4">
              Ihr Konto und alle Chats darin können Sie selbst auf der
              Kontoseite löschen.
            </p>
            <p className="mb-0">
              Kontakt für Rechtewahrnehmung: <ContactLink />
            </p>
          </Section>

          <Section title="12. Minderjährige">
            <p className="mb-0">
              Der Dienst richtet sich an Personen ab 16 Jahren. Jüngere Personen
              benötigen die Zustimmung der Erziehungsberechtigten.
            </p>
          </Section>

          <Section title="13. Datenquellen">
            <p className="mb-0">
              Daten stammen überwiegend direkt von Ihnen. Technische Daten
              fallen bei Nutzung unserer Systeme an. Keine verdeckte
              Drittquellenerhebung.
            </p>
          </Section>

          <Section title="14. Datenschutz-Folgenabschätzung">
            <p className="mb-0">
              Aufgrund der möglichen Verarbeitung von Gesundheitsdaten prüfen
              wir eine Datenschutz-Folgenabschätzung (Art. 35 DSGVO) und
              aktualisieren diese bei wesentlichen Änderungen.
            </p>
          </Section>

          <Section title="15. Änderungen dieser Hinweise">
            <p className="mb-0">
              Wir passen diese Hinweise an, wenn sich Prozesse ändern oder neue
              Dienste hinzukommen. Die aktuelle Fassung ist jederzeit in der App
              unter /privacy abrufbar.
            </p>
          </Section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">
              16. Kontakt
            </h2>
            <p className="mb-4">
              Ivelion Health GmbH <br /> E-Mail: <ContactLink /> <br />
              Anschrift: Altensteinstraße 40, 14195 Berlin, Deutschland
            </p>
          </section>
        </div>

        <div className="mt-12 pt-8 border-t border-gray-200 flex items-center justify-between">
          <Link
            to="/terms"
            className="text-purple-600 hover:text-purple-700 font-medium"
          >
            ← Nutzungsbedingungen
          </Link>
          <Link
            to="/"
            className="text-purple-600 hover:text-purple-700 font-medium"
          >
            Zurück zum Chat →
          </Link>
        </div>
      </div>
    </div>
  );
}
